import React, { useState, useEffect } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.b2bcheck.fr';

interface User {
  _id: string;
  nom: string;
  prenom: string;
  email: string;
  societe: string;
  siret: string;
  tva: string;
  createdAt: string;
  isAdmin?: boolean;
  subscription?: {
    plan: string | null;
    verificationsLeft: number;
    verificationsTotal: number;
    renewalDate: string | null;
  };
  reportCount?: number;
}

interface Report {
  id: string;
  siret: string;
  company_name: string;
  score: number | null;
  checked_at: string;
  risk_level: string;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [selectedUserReports, setSelectedUserReports] = useState<Report[]>([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    societe: '',
    siret: '',
    tva: '',
    password: '',
    isAdmin: false,
    subscriptionPlan: '',
    verificationsLeft: 1,
    verificationsTotal: 1,
    renewalDate: ''
  });

  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    if (adminAuth) {
      setIsAuthenticated(true);
      loadUsers();
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', 'true');
        loadUsers();
      } else {
        setError(data.message || 'Identifiants incorrects');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Erreur chargement utilisateurs:', err);
    }
  };

  const loadUserReports = async (email: string) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/reports`);
      const data = await response.json();
      if (data.success) {
        setSelectedUserReports(data.reports);
        setSelectedUserEmail(email);
        setShowReportsModal(true);
      }
    } catch (err) {
      console.error('Erreur chargement rapports:', err);
    }
  };

  const getScoreColor = (score: number | null) => {
    if (!score || score === 0) return '#f44336';
    if (score <= 5) return '#f44336';
    if (score <= 11) return '#ff9800';
    if (score <= 15) return '#cddc39';
    if (score <= 19) return '#4caf50';
    return '#4caf50';
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Confirmer la suppression ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        loadUsers();
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      nom: user.nom,
      prenom: user.prenom,
      email: user.email,
      societe: user.societe,
      siret: user.siret,
      tva: user.tva,
      password: '',
      isAdmin: user.isAdmin || false,
      subscriptionPlan: user.subscription?.plan || '',
      verificationsLeft: user.subscription?.verificationsLeft || 1,
      verificationsTotal: user.subscription?.verificationsTotal || 1,
      renewalDate: user.subscription?.renewalDate ? new Date(user.subscription.renewalDate).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      societe: '',
      siret: '',
      tva: '',
      password: '',
      isAdmin: false,
      subscriptionPlan: '',
      verificationsLeft: 1,
      verificationsTotal: 1,
      renewalDate: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const url = editingUser
      ? `${API_URL}/api/admin/users/${editingUser._id}`
      : `${API_URL}/api/admin/users`;

    const method = editingUser ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        loadUsers();
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
  };

  if (!isAuthenticated) {
    return (
      <div className="app">
        <div className="login-container">
          <h1>Admin - B2BCheck</h1>
          <form onSubmit={handleLogin}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              required
            />
            {error && <div className="error">{error}</div>}
            <button type="submit">Connexion</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <h1>Admin - B2BCheck</h1>
        <div>
          <button onClick={handleCreate} className="btn-create">Ajouter un client</button>
          <button onClick={handleLogout} className="btn-logout">Déconnexion</button>
        </div>
      </header>

      <div className="container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Entreprise</th>
              <th>Statut abonné</th>
              <th>Date création</th>
              <th>Rapports</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id}>
                <td>
                  <div style={{ fontSize: '0.9rem' }}>
                    <div><strong>{user.nom} {user.prenom}</strong></div>
                    <div style={{ color: '#666', fontSize: '0.85rem' }}>{user.email}</div>
                  </div>
                </td>
                <td>
                  {user.societe ? (
                    <div style={{ fontSize: '0.85rem' }}>
                      <div>{user.societe}</div>
                      <div style={{ color: '#666' }}>SIRET: {user.siret || '-'}</div>
                      <div style={{ color: '#666' }}>TVA: {user.tva || '-'}</div>
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>Particulier</span>
                  )}
                </td>
                <td>
                  {user.subscription ? (
                    <div style={{ fontSize: '0.85rem' }}>
                      <div><strong>{user.subscription.plan || 'Gratuit'}</strong></div>
                      <div>{user.subscription.verificationsLeft}/{user.subscription.verificationsTotal} vérifications</div>
                      {user.subscription.renewalDate && (
                        <div style={{ color: '#666' }}>
                          Renouvellement: {new Date(user.subscription.renewalDate).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999' }}>Non configuré</span>
                  )}
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    onClick={() => loadUserReports(user.email)}
                    className="btn-report-count"
                    title="Voir les rapports"
                  >
                    {user.reportCount || 0}
                  </button>
                </td>
                <td>
                  <button onClick={() => handleEdit(user)} className="btn-edit" title="Modifier">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button onClick={() => handleDelete(user._id)} className="btn-delete" title="Supprimer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? 'Modifier' : 'Créer'} utilisateur</h2>
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="modal-section">
                  <h3>Données utilisateur</h3>

                  <label>
                    <span>Nom</span>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({...formData, nom: e.target.value})}
                      required
                    />
                  </label>

                  <label>
                    <span>Prénom</span>
                    <input
                      type="text"
                      value={formData.prenom}
                      onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                      required
                    />
                  </label>

                  <label>
                    <span>Email</span>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </label>

                  <label>
                    <span>Société</span>
                    <input
                      type="text"
                      value={formData.societe}
                      onChange={(e) => setFormData({...formData, societe: e.target.value})}
                    />
                  </label>

                  <label>
                    <span>SIRET</span>
                    <input
                      type="text"
                      value={formData.siret}
                      onChange={(e) => setFormData({...formData, siret: e.target.value})}
                    />
                  </label>

                  <label>
                    <span>TVA</span>
                    <input
                      type="text"
                      value={formData.tva}
                      onChange={(e) => setFormData({...formData, tva: e.target.value})}
                    />
                  </label>

                  <label>
                    <span>{editingUser ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}</span>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!editingUser}
                    />
                  </label>

                  <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', padding: '10px', background: '#f5f5f5', borderRadius: '4px', cursor: 'pointer', marginTop: '10px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isAdmin}
                      onChange={(e) => setFormData({...formData, isAdmin: e.target.checked})}
                      style={{ width: 'auto', margin: 0, accentColor: '#000' }}
                    />
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, textTransform: 'none' }}>Administrateur</span>
                  </label>
                </div>

                <div className="modal-section">
                  <h3>Abonnement</h3>

                  <label>
                    <span>Plan</span>
                    <select
                      value={formData.subscriptionPlan}
                      onChange={(e) => setFormData({...formData, subscriptionPlan: e.target.value})}
                    >
                      <option value="">Gratuit</option>
                      <option value="Démarrage">Démarrage</option>
                      <option value="Professionnel">Professionnel</option>
                      <option value="Entreprise">Entreprise</option>
                    </select>
                  </label>

                  <div style={{ marginBottom: '15px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#666', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '6px' }}>
                      Vérifications
                    </span>
                    <div style={{ background: '#fff9e6', border: '1px solid #ffc107', padding: '10px 12px', borderRadius: '4px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffc107" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                          <line x1="12" y1="9" x2="12" y2="13"></line>
                          <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                        <small style={{ fontSize: '0.75rem', color: '#856404', lineHeight: 1.4 }}>
                          <strong>Bonus ponctuel :</strong> Ne modifier que "Restantes". <strong>Changement permanent :</strong> Modifier "Restantes" et "Total".
                        </small>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <small style={{ fontSize: '0.7rem', color: '#999', fontStyle: 'italic', display: 'block', marginBottom: '4px' }}>
                          Restantes
                        </small>
                        <input
                          type="number"
                          value={formData.verificationsLeft}
                          onChange={(e) => setFormData({...formData, verificationsLeft: parseInt(e.target.value)})}
                          min="0"
                          style={{ marginBottom: 0 }}
                        />
                        <small style={{ fontSize: '0.72rem', color: '#999', fontStyle: 'italic', display: 'block', marginTop: '6px', lineHeight: 1.4 }}>
                          Nombre de requêtes disponibles actuellement pour cet utilisateur. Ce nombre diminue à chaque vérification effectuée et se réinitialise automatiquement au prochain renouvellement.
                        </small>
                      </div>
                      <span style={{ fontSize: '1.5rem', color: '#999', fontWeight: 300, marginTop: '24px' }}>/</span>
                      <div style={{ flex: 1 }}>
                        <small style={{ fontSize: '0.7rem', color: '#999', fontStyle: 'italic', display: 'block', marginBottom: '4px' }}>
                          Total
                        </small>
                        <input
                          type="number"
                          value={formData.verificationsTotal}
                          onChange={(e) => setFormData({...formData, verificationsTotal: parseInt(e.target.value)})}
                          min="1"
                          style={{ marginBottom: 0 }}
                        />
                        <small style={{ fontSize: '0.72rem', color: '#999', fontStyle: 'italic', display: 'block', marginTop: '6px', lineHeight: 1.4 }}>
                          Nombre de requêtes mensuelles qui sera attribué à chaque renouvellement (tous les mois). Ce nombre définit le nombre maximum de vérifications disponibles par période de facturation.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                    <small style={{ color: '#666' }}>
                      Date de renouvellement : {formData.renewalDate ? new Date(formData.renewalDate).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                    </small>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">Annuler</button>
                <button type="submit" className="btn-submit">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReportsModal && (
        <div className="modal-overlay" onClick={() => setShowReportsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: '900px' }}>
            <h2>Rapports de {selectedUserEmail}</h2>
            {selectedUserReports.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                Aucun rapport trouvé
              </p>
            ) : (
              <table className="users-table" style={{ marginTop: '20px' }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Entreprise</th>
                    <th>SIRET</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUserReports.map(report => (
                    <tr key={report.id}>
                      <td>{new Date(report.checked_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}</td>
                      <td>{report.company_name || '-'}</td>
                      <td>{report.siret}</td>
                      <td>
                        <strong style={{ color: getScoreColor(report.score) }}>
                          {report.score !== null ? report.score : '-'}/20
                        </strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowReportsModal(false)} className="btn-cancel">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
